import { Key, useMemo } from 'react'

import {
  ColumnHeightOutlined,
  VerticalAlignMiddleOutlined
} from '@ant-design/icons'
import { Button, Tree } from 'antd'

import { findTreeMatches } from '@eastcoal/common/utils/array'
import { pick } from '@eastcoal/common/utils/object'

import { useResourceBaseTreeStore } from '../Store/hooks/useStore'

import { ResourceBaseTreeCoreProps } from './interface'

export const ResourceBaseTreeCore = ({
  onlyShowChecked,
  value,
  onChange,
  /* -------------------------------------------------------------------------- */
  /*                               Antd Tree Props                              */
  /* -------------------------------------------------------------------------- */
  ...treeProps
}: ResourceBaseTreeCoreProps) => {
  const { data, isExpanded, expandedKeys, toggleExpandAll, setExpendedKeys } =
    useResourceBaseTreeStore(state =>
      pick(state, [
        'data',
        'expandedKeys',
        'isExpanded',
        'toggleExpandAll',
        'setExpendedKeys'
      ])
    )

  const expanded = isExpanded()

  const checkedKeys = useMemo(() => {
    if (!value) {
      return undefined
    }
    const nodes = findTreeMatches(
      data,
      value,
      (item, uuid) => item.uuid === uuid,
      {
        // 一个 uuid 可能对应多个 route 节点，虽然这么配置是错误的，但是还是要处理
        isUniqueKey: false
      }
    )
    return nodes.map(({ key }) => key)
  }, [data, value])

  const treeData = useMemo(() => {
    if (onlyShowChecked !== true) {
      return data
    }
    const filter = (menus: typeof data) => {
      return menus.reduce(
        (acc, { children, key, ...rest }) => {
          const newChildren = children && filter(children)
          if (checkedKeys?.includes(key) || newChildren?.length) {
            acc.push({ ...rest, key, children: newChildren })
          }
          return acc
        },
        [] as typeof data
      )
    }
    return filter(data)
  }, [data, checkedKeys, onlyShowChecked])

  const handleCheck = (
    checked:
      | {
          checked: Key[]
          halfChecked: Key[]
        }
      | Key[]
  ) => {
    const checkedKeys = Array.isArray(checked) ? checked : checked.checked
    const nodes = findTreeMatches(
      data,
      checkedKeys,
      (item, key) => item.key === key
    )
    const values = nodes
      .map(item => item.uuid)
      .filter((uuid): uuid is string => !!uuid)
    onChange?.(values, nodes)
  }

  return (
    <div>
      <Button
        style={{ marginLeft: '12px' }}
        type='text'
        icon={
          expanded ? <VerticalAlignMiddleOutlined /> : <ColumnHeightOutlined />
        }
        onClick={() => toggleExpandAll()}
      >
        {expanded ? '全部折叠' : '展开所有'}
      </Button>
      <Tree
        checkedKeys={checkedKeys}
        expandedKeys={expandedKeys}
        showIcon={false}
        treeData={treeData}
        onCheck={handleCheck}
        onExpand={setExpendedKeys}
        {...treeProps}
      />
    </div>
  )
}
